import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Edit, Trash2, FolderOpen, X, Upload, Image as ImageIcon, AlertCircle, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase, uploadFile, getPublicUrl } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import type { Category, ConfiguratorTemplate } from '@/types'

const categorySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  description: z.string().optional(),
  sort_order: z.number(),
  configurator_template_id: z.string().nullable(),
  show_filters: z.boolean(),
})

type CategoryFormData = z.infer<typeof categorySchema>

// Sortable Category Item Component
function SortableCategoryItem({
  category,
  onEdit,
  onDelete,
}: {
  category: Category
  onEdit: () => void
  onDelete: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'p-4 flex items-center gap-4 hover:bg-secondary-50 bg-white',
        isDragging && 'opacity-50 shadow-lg z-10 relative'
      )}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="p-2 text-secondary-400 hover:text-secondary-600 cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      <div className="w-16 h-16 bg-secondary-100 rounded-xl overflow-hidden flex-shrink-0">
        {category.image_url ? (
          <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FolderOpen className="w-8 h-8 text-secondary-400" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-secondary-900">{category.name}</h3>
        <p className="text-sm text-secondary-500 truncate">{category.description || 'No description'}</p>
        <p className="text-xs text-secondary-400 mt-1">Slug: {category.slug}</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onEdit}
          className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export function CategoriesPage() {
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [hoverImageFile, setHoverImageFile] = useState<File | null>(null)
  const [hoverImagePreview, setHoverImagePreview] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const { data: categories, isLoading } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order')
      if (error) throw error
      return data as Category[]
    },
  })

  const { data: templates } = useQuery({
    queryKey: ['configurator-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('configurator_templates')
        .select('*')
        .order('name')
      if (error) throw error
      return data as ConfiguratorTemplate[]
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: { name: '', slug: '', description: '', sort_order: 0, configurator_template_id: null, show_filters: true },
  })

  const name = watch('name')

  const openModal = (category?: Category) => {
    if (category) {
      setEditingCategory(category)
      reset({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        sort_order: category.sort_order,
        configurator_template_id: category.configurator_template_id,
        show_filters: category.show_filters !== false,
      })
      setImagePreview(category.image_url)
      setHoverImagePreview(category.hover_image_url || null)
    } else {
      setEditingCategory(null)
      reset({ name: '', slug: '', description: '', sort_order: categories?.length || 0, configurator_template_id: null, show_filters: true })
      setImagePreview(null)
      setHoverImagePreview(null)
    }
    setImageFile(null)
    setHoverImageFile(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingCategory(null)
    setImageFile(null)
    setImagePreview(null)
    setHoverImageFile(null)
    setHoverImagePreview(null)
  }

  const saveMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      let image_url = editingCategory?.image_url || null
      let hover_image_url = editingCategory?.hover_image_url || null

      if (imageFile) {
        const path = `categories/${Date.now()}-${imageFile.name}`
        await uploadFile('categories', path, imageFile)
        image_url = getPublicUrl('categories', path)
      }

      if (hoverImageFile) {
        const path = `categories/${Date.now()}-hover-${hoverImageFile.name}`
        await uploadFile('categories', path, hoverImageFile)
        hover_image_url = getPublicUrl('categories', path)
      }

      const categoryData = {
        name: data.name,
        slug: data.slug,
        description: data.description || null,
        sort_order: data.sort_order,
        configurator_template_id: data.configurator_template_id || null,
        show_filters: data.show_filters,
        image_url,
        hover_image_url,
      }

      if (editingCategory) {
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', editingCategory.id)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('categories')
          .insert(categoryData)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
      closeModal()
    },
  })

  const reorderMutation = useMutation({
    mutationFn: async (reorderedCategories: Category[]) => {
      const updates = reorderedCategories.map((cat, index) => ({
        id: cat.id,
        sort_order: index,
      }))

      for (const update of updates) {
        const { error } = await supabase
          .from('categories')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('categories').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
    },
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id && categories) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id)
      const newIndex = categories.findIndex((cat) => cat.id === over.id)

      const reordered = arrayMove(categories, oldIndex, newIndex)

      // Optimistic update
      queryClient.setQueryData(['admin-categories'], reordered)

      // Save to database
      reorderMutation.mutate(reordered)
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const removeImage = () => {
    setImageFile(null)
    setImagePreview(null)
  }

  const handleHoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setHoverImageFile(file)
      setHoverImagePreview(URL.createObjectURL(file))
    }
  }

  const removeHoverImage = () => {
    setHoverImageFile(null)
    setHoverImagePreview(null)
  }

  // Auto-generate slug
  if (!editingCategory && name) {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    setValue('slug', slug)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Categories</h1>
          <p className="text-secondary-600">Drag to reorder, or manage product categories</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Add Category
        </button>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : categories && categories.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={categories.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="divide-y divide-secondary-100">
                {categories.map((category) => (
                  <SortableCategoryItem
                    key={category.id}
                    category={category}
                    onEdit={() => openModal(category)}
                    onDelete={() => {
                      if (confirm('Delete this category?')) deleteMutation.mutate(category.id)
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="p-8 text-center">
            <FolderOpen className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No categories yet</p>
          </div>
        )}
      </div>

      {/* Reorder Status */}
      {reorderMutation.isPending && (
        <div className="fixed bottom-4 right-4 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          Saving order...
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-xl bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-secondary-900">
                      {editingCategory ? 'Edit Category' : 'New Category'}
                    </h2>
                    <p className="text-sm text-secondary-500">
                      {editingCategory ? 'Update category details' : 'Add a new product category'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-secondary-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-secondary-500" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-5">
                  {/* Name Field */}
                  <div className="form-group">
                    <label className="label">
                      Category Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('name')}
                      placeholder="e.g. Watertight Doors"
                      className={cn('input', errors.name && 'input-error')}
                    />
                    {errors.name && (
                      <p className="error-text">
                        <AlertCircle className="w-4 h-4" />
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* Slug Field */}
                  <div className="form-group">
                    <label className="label">
                      URL Slug <span className="text-red-500">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-4 text-sm text-secondary-500 bg-secondary-100 border-2 border-r-0 border-secondary-200 rounded-l-xl">
                        /products/
                      </span>
                      <input
                        {...register('slug')}
                        placeholder="watertight-doors"
                        className={cn('input rounded-l-none flex-1', errors.slug && 'input-error')}
                      />
                    </div>
                    {errors.slug && (
                      <p className="error-text">
                        <AlertCircle className="w-4 h-4" />
                        {errors.slug.message}
                      </p>
                    )}
                  </div>

                  {/* Description Field */}
                  <div className="form-group">
                    <label className="label">Description</label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      placeholder="Brief description of this category..."
                      className="input resize-none"
                    />
                  </div>

                  {/* Image Upload */}
                  <div className="form-group">
                    <label className="label">Category Image (Primary)</label>
                    {imagePreview ? (
                      <div className="flex items-start gap-4">
                        <div className="image-preview">
                          <img src={imagePreview} alt="Preview" />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="image-preview-remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-secondary-600 mb-2">Image uploaded</p>
                          <label className="btn-outline text-sm cursor-pointer">
                            <Upload className="w-4 h-4 mr-2" />
                            Change Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="sr-only"
                            />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="upload-area cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="sr-only"
                        />
                        <ImageIcon className="w-10 h-10 text-secondary-400 mb-3" />
                        <p className="text-sm font-medium text-secondary-600">
                          Click to upload image
                        </p>
                        <p className="text-xs text-secondary-400 mt-1">
                          PNG, JPG up to 5MB
                        </p>
                      </label>
                    )}
                  </div>

                  {/* Hover Image Upload */}
                  <div className="form-group">
                    <label className="label">Hover Image (Secondary)</label>
                    <p className="text-xs text-secondary-500 mb-2">
                      This image will be shown when hovering over the category card
                    </p>
                    {hoverImagePreview ? (
                      <div className="flex items-start gap-4">
                        <div className="image-preview">
                          <img src={hoverImagePreview} alt="Hover Preview" />
                          <button
                            type="button"
                            onClick={removeHoverImage}
                            className="image-preview-remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-secondary-600 mb-2">Hover image uploaded</p>
                          <label className="btn-outline text-sm cursor-pointer">
                            <Upload className="w-4 h-4 mr-2" />
                            Change Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleHoverImageChange}
                              className="sr-only"
                            />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="upload-area cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleHoverImageChange}
                          className="sr-only"
                        />
                        <ImageIcon className="w-10 h-10 text-secondary-400 mb-3" />
                        <p className="text-sm font-medium text-secondary-600">
                          Click to upload hover image
                        </p>
                        <p className="text-xs text-secondary-400 mt-1">
                          PNG, JPG up to 5MB (optional)
                        </p>
                      </label>
                    )}
                  </div>

                  {/* Show Filters Toggle */}
                  <div className="form-group">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        {...register('show_filters')}
                        className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-secondary-700">Show Filters</span>
                        <p className="text-xs text-secondary-400">
                          Enable filter sidebar on category page
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Configurator Template */}
                  <div className="form-group">
                    <label className="label">Product Configurator Template</label>
                    <select
                      {...register('configurator_template_id')}
                      className="input"
                    >
                      <option value="">No configurator</option>
                      {templates?.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-secondary-400 mt-1">
                      Select a template to show product configurator on product pages
                    </p>
                  </div>

                  {/* Sort Order - Optional */}
                  <div className="form-group">
                    <label className="label">
                      Display Order <span className="text-secondary-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="number"
                      {...register('sort_order', { valueAsNumber: true })}
                      className="input w-32"
                      min={0}
                    />
                    <p className="text-xs text-secondary-400 mt-1">
                      You can also drag & drop to reorder categories
                    </p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50">
                  <button type="button" onClick={closeModal} className="btn-outline">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveMutation.isPending}
                    className="btn-primary min-w-[120px]"
                  >
                    {saveMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      editingCategory ? 'Save Changes' : 'Create Category'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
